import { InteractiveObject } from './InteractiveObject';
import { DisplayObject, HIT_SKIP_INVISIBLE, HIT_SKIP_MASK, MousePick, PICK_MISS, PICK_PROPAGATE, RenderKind } from './DisplayObject';
import { Event } from '../events/Event';
import { rt } from '../player/runtime';
import { rectContains, rectValid } from '../utils/numeric';

function argumentError(msg: string): Error {
  const e = new Error(`ArgumentError: ${msg}`);
  e.name = 'ArgumentError';
  return e;
}
function rangeError(msg: string): RangeError {
  return new RangeError(msg);
}

/** Dispatch ADDED (+ ADDED_TO_STAGE for the subtree when it just arrived on stage). */
export function dispatchAddedEvents(parent: any, child: DisplayObject, childWasOnStage: boolean): void {
  child.$dispatch(new Event(Event.ADDED, true, false));
  if (!childWasOnStage && parent.stage) child.$dispatchRecursive(Event.ADDED_TO_STAGE);
}

/** Dispatch REMOVED (+ REMOVED_FROM_STAGE for the subtree when on stage). Called before removal. */
export function dispatchRemovedEvents(child: DisplayObject): void {
  child.$dispatch(new Event(Event.REMOVED, true, false));
  if (child.stage) child.$dispatchRecursive(Event.REMOVED_FROM_STAGE);
}

/**
 * The timeline clip layer (clipDepth) that masks each child of `kids` (null: the container has none), grouped as the
 * renderer draws them (CanvasRenderer.drawChildren): a clip layer masks the children that follow it in the render
 * list while their depth lies inside its range (an inner clip layer wins for its own range). A child added by script
 * (depth 0, or the depth it had on another timeline) after the clip layers is not masked, and the timeline children
 * below it stay masked for the hit tests too.
 */
function clipLayersOf(kids: readonly DisplayObject[]): Map<DisplayObject, DisplayObject> | null {
  let map: Map<DisplayObject, DisplayObject> | null = null;
  for (let i = 0; i < kids.length; i++) {
    const clip = kids[i];
    if (clip.$clipDepth <= 0) continue;
    for (let j = i + 1; j < kids.length; j++) {
      const d = kids[j].$depth;
      if (d <= clip.$depth || d > clip.$clipDepth) break;
      (map ??= new Map()).set(kids[j], clip);
    }
  }
  return map;
}

/**
 * flash.display.DisplayObjectContainer.
 *
 * Children live in a render list (`$children`, what getChildAt sees). Timeline-placed children are
 * additionally registered in a depth map (`$depthMap`) used to match PlaceObject/RemoveObject ops;
 * script-added children are only in the render list (AVM2 semantics).
 */
export class DisplayObjectContainer extends InteractiveObject {
  /** @internal render list (back to front) */ $children: DisplayObject[];
  /** @internal timeline depth list */ $depthMap: Map<number, DisplayObject> | null;
  /** @internal */ $mouseChildren: boolean;
  /** @internal */ $tabChildren: boolean;

  constructor() {
    super();
    this.$children = [];
    this.$depthMap = null;
    this.$mouseChildren = true;
    this.$tabChildren = true;
  }

  /** @internal */ get $isContainer(): boolean {
    return true;
  }
  /** @internal */ get $renderKind(): RenderKind {
    return 'container';
  }

  get numChildren(): number {
    return this.$children.length;
  }
  get mouseChildren(): boolean {
    return this.$mouseChildren;
  }
  set mouseChildren(v: boolean) {
    this.$mouseChildren = !!v;
  }
  get tabChildren(): boolean {
    return this.$tabChildren;
  }
  set tabChildren(v: boolean) {
    this.$tabChildren = !!v;
  }
  get textSnapshot(): any {
    return null;
  }

  addChild(child: DisplayObject): any {
    if (!child) throw new TypeError('Error #2007: Parameter child must be non-null.');
    const idx = child.$parent === (this as any) ? this.$children.length - 1 : this.$children.length;
    return this.addChildAt(child, idx);
  }

  addChildAt(child: DisplayObject, index: number): any {
    if (!child) throw new TypeError('Error #2007: Parameter child must be non-null.');
    if ((child as any) === this) throw argumentError('Error #2024: An object cannot be added as a child of itself.');
    if ((child as any).$isContainer && (child as any).contains(this)) {
      throw argumentError('Error #2150: An object cannot be added as a child to one of it\'s children (or children\'s children, etc.).');
    }
    if ((child as any).$isStage) throw argumentError('Error #2069: The Stage class does not implement this property or method.');
    index = index | 0;
    const sameParent = child.$parent === (this as any);
    const max = sameParent ? this.$children.length - 1 : this.$children.length;
    if (index < 0 || index > max) throw rangeError('Error #2006: The supplied index is out of bounds.');
    if (sameParent) {
      const cur = this.$children.indexOf(child);
      this.$children.splice(cur, 1);
      this.$children.splice(index, 0, child);
      child.$placedByScript = true;
      this.$invalidateChildren();
      return child;
    }
    const oldParent: any = child.$parent;
    if (oldParent) {
      child.$placedByScript = true;
      oldParent.$removeChildInternal(child, false);
    }
    const wasOnStage = child.stage !== null;
    child.$placedByScript = true;
    child.$placeFrame = 0;
    child.$parent = this as any;
    this.$children.splice(Math.min(index, this.$children.length), 0, child);
    this.$invalidateChildren();
    if (!child.$constructed) {
      // A timeline child moved by script before its construct phase: it is constructed now
      // (no parent-property binding: placed by script).
      child.$constructed = true;
      rt().unconstructed.delete(child);
    }
    dispatchAddedEvents(this, child, wasOnStage);
    return child;
  }

  removeChild(child: DisplayObject): any {
    if (!child) throw new TypeError('Error #2007: Parameter child must be non-null.');
    if (child.$parent !== (this as any) || this.$children.indexOf(child) < 0) {
      throw argumentError('Error #2025: The supplied DisplayObject must be a child of the caller.');
    }
    child.$placedByScript = true;
    this.$removeChildInternal(child, true);
    return child;
  }

  removeChildAt(index: number): any {
    index = index | 0;
    if (index < 0 || index >= this.$children.length) throw rangeError('Error #2006: The supplied index is out of bounds.');
    return this.removeChild(this.$children[index]);
  }

  removeChildren(beginIndex: number = 0, endIndex: number = 0x7fffffff): void {
    const n = this.$children.length;
    if (n === 0 && beginIndex === 0 && endIndex === 0x7fffffff) return;
    if (endIndex === 0x7fffffff) endIndex = n - 1;
    if (beginIndex < 0 || endIndex < 0 || endIndex >= n || beginIndex > endIndex) {
      throw rangeError('Error #2006: The supplied index is out of bounds.');
    }
    const removed = this.$children.slice(beginIndex, endIndex + 1);
    for (const c of removed) if (c.$constructed) dispatchRemovedEvents(c);
    for (const c of removed) {
      c.$placedByScript = true;
      this.$detach(c);
    }
    this.$invalidateChildren();
  }

  getChildAt(index: number): any {
    index = index | 0;
    if (index < 0 || index >= this.$children.length) throw rangeError('Error #2006: The supplied index is out of bounds.');
    return this.$children[index];
  }

  getChildByName(name: string): any {
    for (const c of this.$children) if (c.$name === name) return c;
    return null;
  }

  getChildIndex(child: DisplayObject): number {
    const i = this.$children.indexOf(child);
    if (i < 0) throw argumentError('Error #2025: The supplied DisplayObject must be a child of the caller.');
    return i;
  }

  setChildIndex(child: DisplayObject, index: number): void {
    const cur = this.getChildIndex(child);
    index = index | 0;
    if (index < 0 || index >= this.$children.length) throw rangeError('Error #2006: The supplied index is out of bounds.');
    child.$placedByScript = true;
    if (cur === index) return;
    this.$children.splice(cur, 1);
    this.$children.splice(index, 0, child);
    this.$invalidateChildren();
  }

  swapChildren(a: DisplayObject, b: DisplayObject): void {
    this.swapChildrenAt(this.getChildIndex(a), this.getChildIndex(b));
  }

  swapChildrenAt(i: number, j: number): void {
    i = i | 0;
    j = j | 0;
    const n = this.$children.length;
    if (i < 0 || i >= n || j < 0 || j >= n) throw rangeError('Error #2006: The supplied index is out of bounds.');
    const a = this.$children[i];
    const b = this.$children[j];
    a.$placedByScript = true;
    b.$placedByScript = true;
    this.$children[i] = b;
    this.$children[j] = a;
    this.$invalidateChildren();
  }

  contains(child: DisplayObject): boolean {
    let o: any = child;
    while (o) {
      if (o === this) return true;
      o = o.$parent;
    }
    return false;
  }

  getObjectsUnderPoint(p: { x: number; y: number }): DisplayObject[] {
    const out: DisplayObject[] = [];
    const gx = Math.trunc(p.x * 20);
    const gy = Math.trunc(p.y * 20);
    const visit = (o: any) => {
      if (!o.$isContainer) {
        if (o.$hitTestShape(gx, gy, HIT_SKIP_INVISIBLE)) out.push(o);
        return;
      }
      for (const c of o.$children) visit(c);
    };
    for (const c of this.$children) visit(c);
    return out;
  }

  areInaccessibleObjectsUnderPoint(_p: any): boolean {
    return false;
  }

  // ------------------------------------------------------------------ internals
  /** @internal Remove with REMOVED events; `byScript` controls the named-property null-out rule. */
  $removeChildInternal(child: DisplayObject, _byScript: boolean): void {
    if (child.$constructed) dispatchRemovedEvents(child);
    // A handler may already have moved the child elsewhere.
    if (child.$parent !== (this as any)) return;
    this.$detach(child);
    this.$invalidateChildren();
  }

  /** @internal Unlink without events (render list + depth list + parent property rules). */
  $detach(child: DisplayObject): void {
    const dm = this.$depthMap;
    if (dm && dm.get(child.$depth) === child) dm.delete(child.$depth);
    const i = this.$children.indexOf(child);
    if (i >= 0) this.$children.splice(i, 1);
    // Timeline removal of a named child sets the parent's property to null (unless touched by script).
    if (!child.$placedByScript && child.$explicitName && child.$name) {
      try {
        const cur = (this as any)[child.$name];
        if (cur !== null && cur !== undefined) (this as any)[child.$name] = null;
      } catch {
        /* Flash swallows errors here */
      }
    }
    child.$parent = null;
    if (!child.$constructed) {
      rt().unconstructed.delete(child);
      child.$constructed = true;
    }
    const player = rt().player;
    if (player) player.$onDetached(child);
  }

  /** @internal Timeline: insert `child` at `depth` (Ruffle replace_at_depth). Returns replaced child. */
  $timelineInsert(child: DisplayObject, depth: number): DisplayObject | null {
    const dm = this.$depthMap || (this.$depthMap = new Map());
    const prev = dm.get(depth) ?? null;
    dm.set(depth, child);
    child.$depth = depth;
    child.$parent = this as any;
    if (prev) {
      const pos = this.$children.indexOf(prev);
      if (pos >= 0 && !prev.$placedByScript) {
        this.$children[pos] = child;
        this.$invalidateChildren();
        return prev;
      }
      this.$children.splice(pos >= 0 ? pos + 1 : this.$children.length, 0, child);
      this.$invalidateChildren();
      return null;
    }
    // Insert before the first depth-list child with a greater depth.
    let above: DisplayObject | null = null;
    let aboveDepth = Infinity;
    for (const [d, c] of dm) {
      if (d > depth && d < aboveDepth) {
        aboveDepth = d;
        above = c;
      }
    }
    const pos = above ? this.$children.indexOf(above) : -1;
    if (pos >= 0) this.$children.splice(pos, 0, child);
    else this.$children.push(child);
    this.$invalidateChildren();
    return null;
  }

  /** @internal Timeline RemoveObject at a child. */
  $timelineRemove(child: DisplayObject): void {
    if (child.$placedByScript) {
      const dm = this.$depthMap;
      if (dm && dm.get(child.$depth) === child) dm.delete(child.$depth);
      return;
    }
    this.$removeChildInternal(child, false);
  }

  /** @internal */ $childAtDepth(depth: number): DisplayObject | null {
    return this.$depthMap?.get(depth) ?? null;
  }

  $lifecycleChildren(): DisplayObject[] | null {
    return this.$children;
  }
  $boundsChildren(): DisplayObject[] | null {
    return this.$children;
  }

  $enterFrame(): void {
    const kids = this.$children;
    if (kids.length === 0) return;
    const snap = kids.slice();
    for (let i = snap.length - 1; i >= 0; i--) snap[i].$enterFrame();
  }

  /** @internal Shape test of the vector drawing / own content (Sprite graphics). */
  $hitTestSelf(_gx: number, _gy: number): boolean {
    return false;
  }

  $hitTestShape(gx: number, gy: number, options: number): boolean {
    if (options & HIT_SKIP_INVISIBLE && !this.$visible && !this.$maskee) return false;
    if (options & HIT_SKIP_MASK && this.$maskee) return false;
    const wb = this.$worldBounds();
    if (!rectValid(wb) || !rectContains(wb, gx, gy)) return false;
    if (this.$mask && !this.$mask.$hitTestShape(gx, gy, HIT_SKIP_INVISIBLE)) return false;
    const clips = clipLayersOf(this.$children);
    for (const child of this.$children) {
      if (child.$clipDepth > 0) continue;
      const clip = clips?.get(child);
      if (clip && !clip.$hitTestShape(gx, gy, HIT_SKIP_MASK | HIT_SKIP_INVISIBLE)) continue;
      if (child.$hitTestShape(gx, gy, options)) return true;
    }
    return this.$hitTestSelf(gx, gy);
  }

  /** AVM2 mouse pick (Ruffle MovieClip::mouse_pick_avm2). */
  $mousePick(gx: number, gy: number): MousePick {
    if (!this.$visible) return PICK_MISS;
    if (this.$mask && !this.$mask.$hitTestShape(gx, gy, 0)) return PICK_MISS;
    if (this.$maskee) return PICK_MISS;
    const options = HIT_SKIP_INVISIBLE | HIT_SKIP_MASK;
    const kids = this.$children;
    const clips = clipLayersOf(kids);
    let foundPropagate = false;
    const order: DisplayObject[] = [];
    for (let i = kids.length - 1; i >= 0; i--) if (kids[i].$isInteractive) order.push(kids[i]);
    for (let i = kids.length - 1; i >= 0; i--) if (!kids[i].$isInteractive) order.push(kids[i]);
    for (const child of order) {
      if (child.$clipDepth > 0 || child.$maskee) continue;
      // PORT (wide screens): a design-clipped child is not there outside the stage rectangle.
      if (child.$designClip && !inStageRect(this, gx, gy)) continue;
      let res: MousePick;
      if (child.$isInteractive) res = child.$mousePick(gx, gy);
      else if (child.$hitTestShape(gx, gy, options) && (!child.$mask || child.$mask.$hitTestShape(gx, gy, options))) {
        res = (this as any).$mouseEnabled ? { kind: 'hit', target: this } : PICK_PROPAGATE;
      } else res = PICK_MISS;
      // A child inside a clip layer's range is hit only inside the clip layer's shape.
      const clip = clips?.get(child);
      if (clip && !clip.$hitTestShape(gx, gy, options)) res = PICK_MISS;
      if (res.kind === 'hit') return combineWithParent(res, this);
      if (res.kind === 'propagate') foundPropagate = true;
    }
    if (foundPropagate) return combineWithParent(PICK_PROPAGATE, this);
    const wb = this.$worldBounds();
    if (rectValid(wb) && rectContains(wb, gx, gy) && this.$hitTestSelf(gx, gy)) {
      return (this as any).$mouseEnabled ? { kind: 'hit', target: this } : PICK_PROPAGATE;
    }
    return PICK_MISS;
  }
}

/** A global (stage, twips) point inside the stage rectangle of the stage `o` is on (true off the stage). */
export function inStageRect(o: any, gx: number, gy: number): boolean {
  let st = o;
  while (st && !st.$isStage) st = st.$parent;
  if (!st) return true;
  return gx >= 0 && gy >= 0 && gx <= st.$stageWidth * 20 && gy <= st.$stageHeight * 20;
}

/** Ruffle Avm2MousePick::combine_with_parent. */
export function combineWithParent(res: MousePick, parent: any): MousePick {
  if (res.kind === 'hit') {
    if (parent.$mouseChildren) return res;
    return parent.$mouseEnabled ? { kind: 'hit', target: parent } : PICK_PROPAGATE;
  }
  if (res.kind === 'propagate') return parent.$mouseEnabled ? { kind: 'hit', target: parent } : PICK_PROPAGATE;
  return PICK_MISS;
}
